import axios from "axios";
import store from "./store";
import { logoutUser } from "./actions/authActions";
import { BACKEND_API_BASE_URL, notification } from "./utils/utility";
import { getImpersonatorSession, exitImpersonation } from "./utils/impersonation";
import {
  EVENT_CREATE_LIST_PATH,
  isEventCreateListPath,
  requestQuickSetupPulse,
} from "./utils/quickSetupPulse";

/**
 * CRA injects REACT_APP_* at build time.
 * Prefer the env var directly; fall back to shared utility constant
 * (which also reads REACT_APP_API_BASE_URL) so OAuth/PayPal links stay in sync.
 */
const resolveApiBaseUrl = () => {
  const fromEnv = process.env.REACT_APP_API_BASE_URL;
  if (fromEnv && typeof fromEnv === "string" && fromEnv.trim()) {
    const trimmed = fromEnv.trim();
    return trimmed.endsWith("/") ? trimmed : `${trimmed}/`;
  }
  return BACKEND_API_BASE_URL;
};

const axiosInstance = axios.create({
  baseURL: resolveApiBaseUrl(),
});

const redirectAfterToast = (url) => {
  window.setTimeout(() => {
    window.location.assign(url);
  }, 1400);
};

// Add response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      if (getImpersonatorSession()) {
        // Token expired mid-impersonation — restore the super-admin session cleanly
        exitImpersonation(store.dispatch, null);
        window.location.href = "/super-admin/subscriber";
      } else {
        store.dispatch(logoutUser());
        window.location.href = "/admin/login";
      }
    }

    if (
      error.response?.status === 403 &&
      error.response?.data?.staff_portal_locked === true
    ) {
      window.location.href = "/admin/user-type";
    } else if (
      error.response?.status === 403 &&
      error.response?.data?.setup_incomplete === true
    ) {
      requestQuickSetupPulse();
      const path = window.location?.pathname || "";
      if (!isEventCreateListPath(path)) {
        window.location.assign(EVENT_CREATE_LIST_PATH);
      }
    } else if (
      error.response?.status === 403 &&
      error.response?.data?.payment_integration_required === true
    ) {
      const msg =
        error.response.data.error ||
        error.response.data.message ||
        "Connect Stripe or PayPal before creating a paid event.";
      notification.warning(msg, "Payment integration required");
      redirectAfterToast("/integrations?tab=payments");
    } else if (
      error.response?.status === 403 &&
      error.response?.data?.feature_locked === true
    ) {
      const msg =
        error.response.data.error ||
        "This feature is not included in your current plan. Please upgrade your subscription.";
      notification.upgrade(msg);
      const onStaffPortal =
        typeof window !== "undefined" &&
        window.location?.pathname?.startsWith("/staff");
      redirectAfterToast(
        onStaffPortal ? "/staff/dashboard" : "/admin/manage-subscription"
      );
    }

    // Plan quantity limit reached (events, seats, calendars, round robin)
    if (
      error.response?.status === 403 &&
      error.response?.data?.plan_limit_exceeded === true
    ) {
      const msg =
        error.response.data.error ||
        "You have reached a limit on your current plan. Please upgrade your subscription.";
      notification.warning(msg, "Plan limit reached");
      redirectAfterToast("/admin/manage-subscription");
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
